/** FieldHelp copy for the tag feature's dialogs.
 *
 *  Kept beside the components that use it, mirroring
 *  `features/notifications/fieldHelp.ts`. Story ids are type-checked against
 *  the generated help registry, so a renamed story breaks the build here. */
import type { FieldHelpEntry } from '@/components/FieldHelp';

// ---- Tag category general tab ----------------------------------------------

export const TAG_CATEGORY_HELP = {
  external_identity: {
    title: 'Externally identified',
    body:
      "Tags here mirror an upstream entity and are identified by their External ID (the source system's id) "
      + 'rather than by name, so two tags may legitimately share a display name. Turn this on only for '
      + 'categories whose feed supplies a stable per-entity id, such as a Ventrata option \u2014 never for '
      + 'enumerations whose name IS the identity (language, balance_due, departure, checkin). A multitag '
      + "category must key on custom_id first: with the key field left at 'name', two tags sharing a name "
      + 'collide in the rendering context and one is dropped silently.',
    story: 'tag-categories',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Tag general tab --------------------------------------------------------

export const TAG_HELP = {
  custom_id: {
    title: 'External ID',
    body:
      "The source system's id for the entity this tag mirrors. On an externally identified category this \u2014 "
      + 'not the name \u2014 is what the importer matches on, so changing it re-points the tag at a different '
      + 'upstream entity and the next import acts on the new value. It must be unique within the category; '
      + 'clearing it returns the tag to name identity.',
    story: 'tags-editor',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Tag name alias dialog --------------------------------------------------

export const TAG_NAME_ALIAS_HELP = {
  category: {
    title: 'Category',
    body: 'The tag category this alias applies within, e.g. "reseller". Aliases never cross categories — the same word can mean different things in two of them.',
    story: 'tag-name-aliases',
  },
  alias: {
    title: 'Alias',
    body: 'A tag name as it actually appears in the data, e.g. "Viator.com". Matched case-insensitively and stored lowercased.',
    story: 'tag-name-aliases',
  },
  canonical: {
    title: 'Canonical token',
    body: 'The governed token this name normalises to, e.g. "viator". Conditions reference the token instead of a spelling. Lowercase letters, digits, "_" and "-" only — "Viator.com" is an alias OF a canonical, never a canonical.',
    story: 'tag-name-aliases',
  },
  matchMode: {
    title: 'Match mode',
    body: 'Exact matches the whole tag name. Prefix matches any name starting with the alias, which collapses long combined names like "TripAdvisor Experiences / Viator - USD" in one row. A prefix needs at least three characters — shorter ones sweep in far more tags than intended.',
    story: 'tag-name-aliases',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator the alias applies to. Leave empty for a system-wide alias. A tenant row shadows the system row with the same category and alias — including shadowing it to disabled, which is how a tenant opts out of one it disagrees with.',
    story: 'tag-name-aliases',
  },
  enabled: {
    title: 'Enabled',
    body: 'Disabled aliases contribute nothing. Disabling is the reversible alternative to deleting, and on a tenant row it is how you cancel a system alias.',
    story: 'tag-name-aliases',
  },
} as const satisfies Record<string, FieldHelpEntry>;

// ---- Canonical dialog -------------------------------------------------------

export const TAG_CANONICAL_HELP = {
  category: {
    title: 'Category',
    body: 'The tag category this canonical belongs to, e.g. "reseller". Canonicals never cross categories — the same token can mean different things in two of them.',
    story: 'tag-canonicals',
  },
  token: {
    title: 'Canonical token',
    body: 'The governed token aliases normalise names onto, e.g. "viator". Lowercase letters, digits, "_" and "-" only. The token is always an implicit alias of itself, so a tag literally named "viator" resolves here with no alias row.',
    story: 'tag-canonicals',
  },
  operator: {
    title: 'Operator',
    body: 'Which tour operator this canonical applies to. Leave empty for a system-wide one. A tenant row shadows the system row with the same category and token — including shadowing it to disabled, which is how a tenant declines a system canonical instead of inheriting its attributes.',
    story: 'tag-canonicals',
  },
  data: {
    title: 'Attributes',
    body: "Attributes that belong to the reseller rather than to any one spelling of its name. A tag's effective attributes are its own data over these, key by key, with the tag winning — so a policy written once here covers every spelling, including one ingestion mints next week. Governed by the same category schema as the tags; disagreements are shown but never block the save.",
    story: 'tag-canonicals',
  },
  enabled: {
    title: 'Enabled',
    body: 'A disabled canonical contributes no attributes. Disabling is the reversible alternative to deleting, and on a tenant row it is how you decline a system canonical.',
    story: 'tag-canonicals',
  },
} as const satisfies Record<string, FieldHelpEntry>;
